import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useAuth } from '../../public/site/AuthProvider.js';
import { useSessionData } from '../../public/site/SessionDataProvider.js';
import { useOnlineModelsContext } from '../../public/site/onlineModel/onlineModelContextProvider.js';
import { addToCart } from '../../selfservice/actions';
import { ProductDetails } from './ProductDetails.js';
import { PRODUCT_DETAILS_CLASS } from './productDetailsConstants.js';
import type { ProductDetailsData } from '../../content-blocks/cmsSchema.js';
import type { OnlineModel } from '../../generated/api/models.js';

import './ProductDetailsPublicLoader.scss';

export interface ProductDetailsPublicLoaderProps extends ProductDetailsData {
  onlineModel: OnlineModel;
}

export const ProductDetailsPublicLoader = ({ eCommerceEvents, onlineModel }: ProductDetailsPublicLoaderProps) => {
  const dispatch = useDispatch();
  const { authenticatedUser, isAuthenticated, ssoSessionValid } = useAuth();
  const { priceAlteredModels, fetchModelsByCodes } = useOnlineModelsContext();
  const companyInfo = useSessionData().companyInfo;
  // Product details page already has a public model in the page payload, so only switch to context-driven data
  // when the user has an account that can resolve private pricing.
  const shouldUsePrivateModel = Boolean(ssoSessionValid && authenticatedUser?.activeAccountMasterId);
  const privateOnlineModel = priceAlteredModels.onlineModelsByCode?.[onlineModel.onlineModelCode];

  useEffect(() => {
    if (shouldUsePrivateModel && onlineModel.onlineModelCode) {
      fetchModelsByCodes([onlineModel.onlineModelCode]);
    }
  }, [fetchModelsByCodes, onlineModel.onlineModelCode, shouldUsePrivateModel]);

  const resolvedOnlineModel = shouldUsePrivateModel ? (privateOnlineModel ?? onlineModel) : onlineModel;
  // When private pricing is expected but not yet loaded, hold back offers/prices (PDP renders a loader).
  const showOfferAndPrices = shouldUsePrivateModel ? Boolean(privateOnlineModel) : true;

  return (
    <div className={`${PRODUCT_DETAILS_CLASS}-public-loader`}>
      <ProductDetails
        onlineModel={resolvedOnlineModel}
        addToCart={cartItemContainer => dispatch(addToCart(cartItemContainer))}
        eCommerceEvents={eCommerceEvents}
        isPublicSite={true}
        isEmployee={false}
        user={isAuthenticated ? authenticatedUser : undefined}
        companyInfo={companyInfo ?? undefined}
        showOfferAndPrices={showOfferAndPrices}
      />
    </div>
  );
};
